import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { GROUPING_ORDER, promptsFor } from "./reflectionGroupings";
import GuidedRibbon from "./GuidedRibbon";

/**
 * ProgressivePromptsStrip — the four-grouping accordion that sits between
 * the framing block and the governed Current Reflection inside the
 * Reflection Canvas article.
 *
 * Extracted verbatim from `ReflectionCanvasArticle.jsx` during the
 * post-Phase-B split. Every `data-testid`, tailwind class, editorial
 * typography choice, and control label is preserved so the existing
 * Phase-B pytest + Playwright suites keep passing.
 *
 * The strip renders the SAME four groupings for every movement or
 * continuous dynamic, in a fixed editorial order:
 *   I   · Present Position
 *   II  · System Dynamics
 *   III · Strategic Leverage
 *   IV  · Next Move
 *
 * One grouping open at a time. All saves land in the Working Journal
 * (provisional). Guided mode is expressed here purely as UI (auto-open
 * the active step's grouping + swap the button set for guided controls);
 * the state machine (`guidedStep`, advance / skip / back) lives in the
 * parent so the digest capability (Phase C) can inspect the same source
 * of truth without prop-drilling.
 *
 * Props (all required unless noted):
 *   testidBase          — base id prefix, e.g. `pinned-movement-reveal`
 *   pinnedNodeType      — "movement" | "dynamic"
 *   pinnedNodeKey       — canonical key on the pinned node
 *   isMovement          — bool, drives the copy in the strip header
 *   openGroupingKey     — currently-expanded grouping key (or null)
 *   setOpenGroupingKey  — setter for open key
 *   groupingDrafts      — { [key]: draftText }
 *   setGroupingDrafts   — setter for drafts map
 *   savingGrouping      — bool, disables save buttons while POSTing
 *   guidedMode          — bool, guided walkthrough active
 *   guidedStep          — current guided step (0-based)
 *   saveGroupingEntry   — async (g) => void — POSTs to Working Journal
 *   skipGuidedStep      — () => void — advances guided step, no POST
 *   backGuidedStep      — () => void — retreats guided step
 *   exitGuidedMode      — () => void — leaves guided cleanly
 */
export default function ProgressivePromptsStrip({
  testidBase,
  pinnedNodeType,
  pinnedNodeKey,
  isMovement,
  openGroupingKey,
  setOpenGroupingKey,
  groupingDrafts,
  setGroupingDrafts,
  savingGrouping,
  guidedMode,
  guidedStep,
  saveGroupingEntry,
  skipGuidedStep,
  backGuidedStep,
  exitGuidedMode,
}) {
  return (
    <div
      className="border-t border-border px-5 md:px-8 py-5 md:py-6"
      data-testid={`${testidBase}-groupings`}
    >
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary">
          § Reflect on this {isMovement ? "movement" : "dynamic"}
        </div>
        {guidedMode && (
          <GuidedRibbon
            step={guidedStep}
            total={GROUPING_ORDER.length}
            dataTestId={`${testidBase}-guided-ribbon`}
          />
        )}
      </div>
      <div className="divide-y divide-border">
        {GROUPING_ORDER.map((g, idx) => {
          const isOpen = openGroupingKey === g.key;
          const list = promptsFor(pinnedNodeType, pinnedNodeKey, g.key);
          const groupingDraft = groupingDrafts[g.key] || "";
          const isActiveGuidedStep = guidedMode && guidedStep === idx;
          return (
            <div key={g.key} data-testid={`${testidBase}-grouping-${g.key}`}>
              <button
                type="button"
                onClick={() => {
                  // In guided mode, clicking an out-of-sequence grouping
                  // exits the walk — the advisor is signaling they want
                  // to jump around freely.
                  if (guidedMode && !isActiveGuidedStep) {
                    exitGuidedMode();
                  }
                  setOpenGroupingKey(isOpen ? null : g.key);
                }}
                data-testid={`${testidBase}-grouping-toggle-${g.key}`}
                aria-expanded={isOpen}
                className={`w-full flex items-baseline justify-between py-3 text-left group ${
                  isActiveGuidedStep ? "bg-brass/5 -mx-2 px-2" : ""
                }`}
              >
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                    § {g.roman}
                  </span>
                  <span
                    className="text-lg font-bold tracking-tight truncate"
                    style={{
                      fontFamily: "'Playfair Display', 'Merriweather', Georgia, serif",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {g.label}
                  </span>
                </div>
                <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground group-hover:text-primary shrink-0">
                  {isOpen ? "close" : "open"}
                </span>
              </button>
              {isOpen && (
                <div className="pb-5 pl-6 md:pl-8 border-l border-brass/30 ml-1 md:ml-2 space-y-3">
                  <ul
                    className="space-y-1.5 max-w-2xl"
                    data-testid={`${testidBase}-grouping-prompts-${g.key}`}
                  >
                    {list.map((p, i) => (
                      <li
                        key={i}
                        className="text-base leading-relaxed italic text-foreground/90 flex gap-2"
                        style={{
                          fontFamily: "'Cormorant Garamond', 'Merriweather', serif",
                          fontWeight: 500,
                          fontSize: "1.0625rem",
                        }}
                        data-testid={`${testidBase}-grouping-prompt-${g.key}-${i}`}
                      >
                        <span className="text-brass shrink-0" aria-hidden>·</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Textarea
                    value={groupingDraft}
                    onChange={(e) =>
                      setGroupingDrafts((s) => ({ ...s, [g.key]: e.target.value }))
                    }
                    placeholder={
                      guidedMode
                        ? "Note what surfaces — or use Skip to move on. Nothing here overwrites the Current Reflection."
                        : "Write what surfaces here — saves to your Working Journal."
                    }
                    data-testid={`${testidBase}-grouping-textarea-${g.key}`}
                    className="min-h-[100px] rounded-none bg-secondary/30 border-border/60 focus-visible:border-primary text-base"
                    style={{ fontFamily: "'Merriweather', serif" }}
                  />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {guidedMode ? (
                      <>
                        <button
                          type="button"
                          onClick={() => saveGroupingEntry(g)}
                          disabled={savingGrouping || !groupingDraft.trim()}
                          data-testid={`${testidBase}-guided-save-continue`}
                          className="text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 disabled:opacity-40"
                        >
                          {savingGrouping
                            ? "Saving…"
                            : guidedStep === GROUPING_ORDER.length - 1
                            ? "Save & finish"
                            : "Save & continue →"}
                        </button>
                        <button
                          type="button"
                          onClick={skipGuidedStep}
                          data-testid={`${testidBase}-guided-skip`}
                          className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary border-b border-transparent hover:border-primary pb-0.5"
                        >
                          {guidedStep === GROUPING_ORDER.length - 1
                            ? "Skip · finish"
                            : "Skip this →"}
                        </button>
                        {guidedStep > 0 && (
                          <button
                            type="button"
                            onClick={backGuidedStep}
                            data-testid={`${testidBase}-guided-back`}
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
                          >
                            ← Back
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={exitGuidedMode}
                          data-testid={`${testidBase}-guided-exit`}
                          className="ml-auto text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
                        >
                          Exit guided ×
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => saveGroupingEntry(g)}
                        disabled={savingGrouping || !groupingDraft.trim()}
                        data-testid={`${testidBase}-grouping-save-${g.key}`}
                        className="text-[0.6875rem] font-mono uppercase tracking-widest text-brass border-b border-brass pb-1 hover:text-foreground hover:border-foreground disabled:opacity-40 disabled:hover:text-brass disabled:hover:border-brass"
                      >
                        {savingGrouping ? "Saving…" : "Save to Working Journal"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
